import React, { useEffect, useRef, useState } from "react";
import apiFetch from "./utils/api";

// ModalConfirm: simple in-app replacement for window.confirm
function ModalConfirm({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div style={styles.modalBackdrop} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{title}</h3>
        <div style={{ marginBottom: 16 }}>{message}</div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel}>Cancel</button>
          <button
            onClick={onConfirm}
            style={{ background: "#c0392b", color: "white" }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// Simple burger Menu component
function Menu({ children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);
  return (
    <div style={{ position: "relative", display: "inline-block" }} ref={ref}>
      <button onClick={() => setOpen((v) => !v)} style={styles.burgerButton}>
        ☰
      </button>
      {open && <div style={styles.menu}>{children}</div>}
    </div>
  );
}

function CreateUserPanel({ onCreated }) {
  const [user, setUser] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [manager, setManager] = useState("");
  const [status, setStatus] = useState("");
  const [managers, setManagers] = useState([]);
  const [utCodes, setUtCodes] = useState([]);
  const [shifts, setShifts] = useState({
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false,
    sun: false,
  });
  const [dailyHours, setDailyHours] = useState(8);
  const [active, setActive] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function loadLists() {
      try {
        const m = await apiFetch("/api/managers");
        const mgrs = Array.isArray(m)
          ? m
          : m && Array.isArray(m.managers)
            ? m.managers
            : [];
        if (mounted) setManagers(mgrs);
      } catch (e) {
        console.warn("Could not load managers", e);
      }
      try {
        const u = await apiFetch("/api/utcodes");
        const codes = Array.isArray(u)
          ? u
          : u && Array.isArray(u.utCodes)
            ? u.utCodes
            : [];
        if (mounted) setUtCodes(codes);
      } catch (e) {
        console.warn("Could not load ut codes", e);
      }
    }
    loadLists();
    return () => {
      mounted = false;
    };
  }, []);

  async function submit(e) {
    e.preventDefault();
    setStatus("creating");
    if (!user || !email) {
      setStatus("User code and password are required");
      return;
    }
    try {
      // Create user
      await apiFetch("/api/users", {
        method: "POST",
        body: {
          user: String(user).trim(),
          desc: name || String(user).trim(),
          ut: role || null,
          email: email,
          hours: dailyHours,
          shift:
            0 +
            (shifts.mon ? 1 : 0) +
            (shifts.tue ? 2 : 0) +
            (shifts.wed ? 4 : 0) +
            (shifts.thu ? 8 : 0) +
            (shifts.fri ? 16 : 0) +
            (shifts.sat ? 32 : 0) +
            (shifts.sun ? 64 : 0),
          active: 1,
          manager: manager || null,
        },
      });
      // If shifts set or dailyHours, call PUT to update flags
      const anyShift = Object.values(shifts).some(Boolean);
      const putBody = {};
      if (anyShift) {
        putBody.Shift_Mon = shifts.mon ? 1 : 0;
        putBody.Shift_Tue = shifts.tue ? 1 : 0;
        putBody.Shift_Wed = shifts.wed ? 1 : 0;
        putBody.Shift_Thu = shifts.thu ? 1 : 0;
        putBody.Shift_Fri = shifts.fri ? 1 : 0;
        putBody.Shift_Sat = shifts.sat ? 1 : 0;
        putBody.Shift_Sun = shifts.sun ? 1 : 0;
      }
      if (
        typeof dailyHours !== "undefined" &&
        dailyHours !== null &&
        dailyHours !== ""
      ) {
        putBody.dailyHours = Number(dailyHours);
      }
      if (Object.keys(putBody).length > 0) {
        await apiFetch(
          `/api/users/${encodeURIComponent(String(user).trim())}`,
          { method: "PUT", body: putBody },
        );
      }
      setUser("");
      setName("");
      setEmail("");
      setRole("");
      setManager("");
      setShifts({
        mon: false,
        tue: false,
        wed: false,
        thu: false,
        fri: false,
        sat: false,
        sun: false,
      });
      setStatus("created");
      onCreated && onCreated();
    } catch (err) {
      setStatus(err && err.message ? err.message : String(err));
    }
  }

  return (
    <form onSubmit={submit} style={styles.panel}>
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          placeholder="User code"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          style={{ minWidth: 120 }}
        />
        <input
          placeholder="Full name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">(select role)</option>
          {utCodes && utCodes.length > 0 ? (
            utCodes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))
          ) : (
            <>
              <option>User</option>
              <option>Admin</option>
              <option>Senior Admin</option>
            </>
          )}
        </select>
        <select
          value={manager || ""}
          onChange={(e) => setManager(e.target.value)}
        >
          <option value="">(no manager)</option>
          {managers.map((m) => (
            <option key={m.code} value={m.code}>
              {m.desc}
            </option>
          ))}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Active
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(!!e.target.checked)}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Daily hrs
          <input
            type="number"
            min={0}
            max={24}
            step={0.25}
            value={dailyHours}
            onChange={(e) => setDailyHours(e.target.value)}
            style={{ width: 80 }}
          />
        </label>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.mon}
              onChange={(e) =>
                setShifts((s) => ({ ...s, mon: e.target.checked }))
              }
            />{" "}
            Mon
          </label>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.tue}
              onChange={(e) =>
                setShifts((s) => ({ ...s, tue: e.target.checked }))
              }
            />{" "}
            Tue
          </label>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.wed}
              onChange={(e) =>
                setShifts((s) => ({ ...s, wed: e.target.checked }))
              }
            />{" "}
            Wed
          </label>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.thu}
              onChange={(e) =>
                setShifts((s) => ({ ...s, thu: e.target.checked }))
              }
            />{" "}
            Thu
          </label>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.fri}
              onChange={(e) =>
                setShifts((s) => ({ ...s, fri: e.target.checked }))
              }
            />{" "}
            Fri
          </label>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.sat}
              onChange={(e) =>
                setShifts((s) => ({ ...s, sat: e.target.checked }))
              }
            />{" "}
            Sat
          </label>
          <label style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={shifts.sun}
              onChange={(e) =>
                setShifts((s) => ({ ...s, sun: e.target.checked }))
              }
            />{" "}
            Sun
          </label>
        </div>
        <button type="submit">Create</button>
      </div>
      {status && status !== "created" && status !== "" && (
        <div style={{ color: "darkred", marginTop: 8 }}>{String(status)}</div>
      )}
    </form>
  );
}

// ManageUsers: lists users and provides Edit / Disable / Delete actions
function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState({ open: false });
  const [editing, setEditing] = useState(null); // user object being edited
  const [resetting, setResetting] = useState(null); // user object for password reset
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  async function load() {
    setLoading(true);
    try {
      const data = await apiFetch("/api/users?inactive=true");
      // backend may return either an array or an object { users: [...] }
      const src = Array.isArray(data)
        ? data
        : data && Array.isArray(data.users)
          ? data.users
          : [];
      // normalize each record to expected UI fields used elsewhere in this file
      const norm = (src || []).map((r) => ({
        US_Code: r.UserCode ?? r.id ?? r.user,
        US_Desc: r.UserDescription ?? r.desc ?? r.user ?? "",
        UT_Code: r.UserRole ?? r.ut ?? null,
        Active:
          typeof r.UserActive !== "undefined"
            ? !!r.UserActive
            : typeof r.active !== "undefined"
              ? !!r.active
              : false,
        Manager: r.UserManager ?? r.manager ?? null,
        // keep original as fallback
        __raw: r,
      }));
      setUsers(norm);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // reset page when search, activeFilter or users change
  useEffect(() => {
    setPage(0);
  }, [search, activeFilter, users]);

  const filtered = users.filter((u) => {
    if (activeFilter === "yes" && !u.Active) return false;
    if (activeFilter === "no" && u.Active) return false;
    if (!search || String(search).trim() === "") return true;
    const s = String(search).toLowerCase();
    return (
      String(u.US_Desc || "")
        .toLowerCase()
        .includes(s) ||
      String(u.US_Code || "")
        .toLowerCase()
        .includes(s)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(Math.max(0, page), Math.max(0, totalPages - 1));
  const paged = filtered.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );

  function ask(action, user) {
    setConfirm({ open: true, action, user });
  }

  async function doConfirm() {
    const { action, user } = confirm;
    setConfirm({ open: false });
    if (action === "disable" || action === "delete") {
      try {
        // soft delete: mark inactive
        await apiFetch(`/api/users/${encodeURIComponent(user.US_Code)}`, {
          method: "DELETE",
        });
        load();
      } catch (err) {
        console.error(err);
      }
    }
  }

  return (
    <section style={{ marginTop: 12 }}>
      <div style={{ background: "#fbfbfe", borderRadius: 8, padding: 12 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              placeholder="Search name, role or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ddd",
                minWidth: 240,
              }}
            />
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ddd",
              }}
            >
              <option value="all">All</option>
              <option value="yes">Active: Yes</option>
              <option value="no">Active: No</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              {filtered.length} result{filtered.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage <= 0}
                style={styles.menuItem}
              >
                Prev
              </button>
              <div style={{ fontSize: 13 }}>
                {safePage + 1} / {totalPages}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                style={styles.menuItem}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th>Name</th>
                <th>Role</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((u) => (
                <tr key={u.US_Code} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: 8 }}>{u.US_Desc}</td>
                  <td style={{ padding: 8 }}>{u.UT_Code}</td>
                  <td style={{ padding: 8 }}>{u.Active ? "Yes" : "No"}</td>
                  <td style={{ padding: 8 }}>
                    <Menu>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 160,
                        }}
                      >
                        <button
                          onClick={() => {
                            setEditing(u);
                          }}
                          style={styles.menuItem}
                        >
                          Edit
                        </button>
                        {/* {                        <button
                          onClick={() => setResetting(u)}
                          style={styles.menuItem}
                        >
                          Reset Password
                        </button>} */}
                        <button
                          onClick={() => ask("disable", u)}
                          style={styles.menuItem}
                        >
                          Disable
                        </button>
                        <button
                          onClick={() => ask("delete", u)}
                          style={{ ...styles.menuItem, color: "#c0392b" }}
                        >
                          Delete
                        </button>
                      </div>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ModalConfirm
        open={confirm.open}
        title={confirm.action === "delete" ? "Delete user" : "Disable user"}
        message={
          confirm.user
            ? `Are you sure you want to ${confirm.action} ${confirm.user.US_Desc}?`
            : ""
        }
        onConfirm={doConfirm}
        onCancel={() => setConfirm({ open: false })}
      />

      {editing && (
        <EditUserOverlay
          user={editing}
          onClose={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {resetting && (
        <ResetPasswordOverlay
          user={resetting}
          onClose={() => {
            setResetting(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function EditUserOverlay({ user, onClose }) {
  const [form, setForm] = useState({
    desc: user.US_Desc,
    ut: user.UT_Code,
    active: !!user.Active,
  });
  const [saving, setSaving] = useState(false);
  const [utCodes, setUtCodes] = useState([]);

  useEffect(() => {
    let mounted = true;
    async function loadUtCodes() {
      try {
        const data = await apiFetch("/api/utcodes");
        const codes = Array.isArray(data)
          ? data
          : data && Array.isArray(data.utCodes)
            ? data.utCodes
            : [];
        if (mounted && Array.isArray(codes)) setUtCodes(codes);
      } catch (e) {
        console.warn("Could not load UT codes", e);
      }
    }
    loadUtCodes();
    return () => {
      mounted = false;
    };
  }, []);

  async function save() {
    setSaving(true);
    try {
      // send ut as UT_Code mapping on backend
      await apiFetch(`/api/users/${encodeURIComponent(user.US_Code)}`, {
        method: "PUT",
        body: { desc: form.desc, ut: form.ut, active: form.active ? 1 : 0 },
      });
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Edit User</h3>
        <div style={{ display: "grid", gap: 8 }}>
          <label>
            Full name
            <input
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
            />
          </label>
          <label>
            Role
            <select
              value={form.ut || ""}
              onChange={(e) => setForm({ ...form, ut: e.target.value })}
            >
              {utCodes && utCodes.length > 0 ? (
                utCodes.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))
              ) : (
                <>
                  <option>User</option>
                  <option>Admin</option>
                  <option>Senior Admin</option>
                </>
              )}
            </select>
          </label>
          <label>
            Active
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onClose}>Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              style={{ background: "#2ecc71", color: "white" }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordOverlay({ user, onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  async function doReset() {
    setStatus("");
    if (!newPassword) return setStatus("Password required");
    if (newPassword !== confirm) return setStatus("Passwords do not match");
    setSaving(true);
    try {
      await apiFetch(
        `/api/users/${encodeURIComponent(user.US_Code)}/password`,
        {
          method: "PUT",
          body: { newPassword },
        },
      );
      setStatus("Password updated");
      onClose();
    } catch (err) {
      setStatus(err && err.message ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Reset Password for {user.US_Desc || user.US_Code}</h3>
        <div style={{ display: "grid", gap: 8 }}>
          <label>
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </label>
          <label>
            Confirm password
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
          {status && <div style={{ color: "darkred" }}>{status}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onClose}>Cancel</button>
            <button
              onClick={doReset}
              disabled={saving}
              style={{ background: "#2ecc71", color: "white" }}
            >
              {saving ? "Saving..." : "Reset Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// CreateProjectPanel, ManageProjects, EditProjectOverlay
function CreateProjectPanel({ onCreated }) {
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [client, setClient] = useState("");
  const [division, setDivision] = useState("");
  const [manager, setManager] = useState("");
  const [estTime, setEstTime] = useState("");
  const [value, setValue] = useState("");
  const [isRD, setIsRD] = useState(false);
  const [chargable, setChargable] = useState(false);
  const [active, setActive] = useState(true);
  const [status, setStatus] = useState("");

  async function submit(e) {
    e && e.preventDefault();
    setStatus("creating");
    if (!code) {
      setStatus("Project code required");
      return;
    }
    try {
      await apiFetch("/api/projects", {
        method: "POST",
        body: {
          project: String(code).trim(),
          desc: desc,
          client: client,
          division: division,
          manager: manager,
          est_time: estTime !== "" ? Number(estTime) : null,
          value: value !== "" ? Number(value) : null,
          isResearchOrDevelopment: isRD ? 1 : 0,
          isChargable: chargable ? 1 : 0,
          active: active ? 1 : 0,
        },
      });
      setCode("");
      setDesc("");
      setClient("");
      setDivision("");
      setManager("");
      setEstTime("");
      setValue("");
      setIsRD(false);
      setChargable(false);
      setActive(true);
      setStatus("created");
      onCreated && onCreated();
    } catch (e) {
      setStatus(e && e.message ? e.message : String(e));
    }
  }

  return (
    <form onSubmit={submit} style={styles.panel}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          placeholder="Project code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ minWidth: 120 }}
        />
        <input
          placeholder="Description"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />
        <input
          placeholder="Client"
          value={client}
          onChange={(e) => setClient(e.target.value)}
        />
        <input
          placeholder="Division"
          value={division}
          onChange={(e) => setDivision(e.target.value)}
          style={{ width: 120 }}
        />
        <input
          placeholder="Project Manager"
          value={manager}
          onChange={(e) => setManager(e.target.value)}
        />
        <input
          placeholder="Est. Days"
          type="number"
          min="0"
          value={estTime}
          onChange={(e) => setEstTime(e.target.value)}
          style={{ width: 100 }}
        />
        <input
          placeholder="Project Value"
          type="number"
          min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: 110 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
          R&amp;D
          <input
            type="checkbox"
            checked={isRD}
            onChange={(e) => setIsRD(!!e.target.checked)}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Chargeable
          <input
            type="checkbox"
            checked={chargable}
            onChange={(e) => setChargable(!!e.target.checked)}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Active
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(!!e.target.checked)}
          />
        </label>
        <button type="submit">Create</button>
      </div>
      {status && status !== "created" && (
        <div style={{ color: "darkred", marginTop: 8 }}>{String(status)}</div>
      )}
    </form>
  );
}

function ManageProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState({ open: false });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  async function load() {
    setLoading(true);
    try {
      const d = await apiFetch("/api/projects?inactive=true");
      const src = d && Array.isArray(d.projects) ? d.projects : [];
      setProjects(
        src.map((p) => ({
          code: p.code,
          desc: p.desc,
          client: p.client,
          division: p.division,
          active: !!p.active,
          __raw: p,
        })),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search, activeFilter, projects]);

  const filtered = projects.filter((p) => {
    if (activeFilter === "yes" && !p.active) return false;
    if (activeFilter === "no" && p.active) return false;
    if (!search || String(search).trim() === "") return true;
    const s = String(search).toLowerCase();
    return (
      String(p.desc || "")
        .toLowerCase()
        .includes(s) ||
      String(p.code || "")
        .toLowerCase()
        .includes(s) ||
      String(p.division || "")
        .toLowerCase()
        .includes(s)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(Math.max(0, page), Math.max(0, totalPages - 1));
  const paged = filtered.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );

  function ask(action, proj) {
    setConfirm({ open: true, action, proj });
  }

  async function doConfirm() {
    const { action, proj } = confirm;
    setConfirm({ open: false });
    if (action === "delete" || action === "disable") {
      try {
        await apiFetch(`/api/projects/${encodeURIComponent(proj.code)}`, {
          method: "DELETE",
        });
        load();
      } catch (e) {
        console.error(e);
      }
    }
    if (action === "enable") {
      try {
        await apiFetch(`/api/projects/${encodeURIComponent(proj.code)}`, {
          method: "PATCH",
        });
        load();
      } catch (e) {
        console.error(e);
      }
    }
  }

  return (
    <section style={{ marginTop: 12 }}>
      <div style={{ background: "#fbfbfe", borderRadius: 8, padding: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              placeholder="Search project or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ddd",
                minWidth: 240,
              }}
            />
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ddd",
              }}
            >
              <option value="all">All</option>
              <option value="yes">Active: Yes</option>
              <option value="no">Active: No</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              {filtered.length} result{filtered.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage <= 0}
                style={styles.menuItem}
              >
                Prev
              </button>
              <div style={{ fontSize: 13 }}>
                {safePage + 1} / {totalPages}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                style={styles.menuItem}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th>Code</th>
                <th>Description</th>
                <th>Division / Client</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((p) => (
                <tr key={p.code} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: 8 }}>{p.code}</td>
                  <td style={{ padding: 8 }}>{p.desc}</td>
                  <td style={{ padding: 8, fontSize: 12 }}>
                    <b>{p.division || "-"}</b>
                    <br />
                    {p.client || "-"}
                  </td>
                  <td style={{ padding: 8 }}>{p.active ? "Yes" : "No"}</td>
                  <td style={{ padding: 8 }}>
                    <Menu>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 160,
                        }}
                      >
                        <button
                          onClick={() => setEditing(p)}
                          style={styles.menuItem}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            ask(p.active ? "disable" : "enable", p)
                          }
                          style={{ ...styles.menuItem, color: "#c0392b" }}
                        >
                          {p.active ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ModalConfirm
        open={confirm.open}
        title={
          confirm.action === "delete" ? "Delete project" : "Disable project"
        }
        message={
          confirm.proj
            ? `Are you sure you want to ${confirm.action} ${
                confirm.proj.desc || confirm.proj.code
              }?`
            : ""
        }
        onConfirm={doConfirm}
        onCancel={() => setConfirm({ open: false })}
      />

      {editing && (
        <EditProjectOverlay
          project={editing}
          onClose={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function EditProjectOverlay({ project, onClose }) {
  const [form, setForm] = useState({
    desc: project.desc,
    client: project.client,
    division: project.division,
    manager: project.__raw?.manager ?? project.__raw?.ProjectManager ?? "",
    est_time: project.__raw?.est_time ?? project.__raw?.ProjectEstTime ?? "",
    value: project.__raw?.value ?? project.__raw?.ProjectValue ?? "",
    rd_flag: !!(
      project.__raw?.isResearchOrDevelopment ??
      project.__raw?.rd_flag ??
      project.__raw?.ProjectIsRD
    ),
    chargable: !!(
      project.__raw?.isChargable ??
      project.__raw?.chargable ??
      project.__raw?.ProjectChargable
    ),
    active: !!project.active,
  });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  async function save() {
    setSaving(true);
    setStatus("");
    try {
      await apiFetch(`/api/projects/${encodeURIComponent(project.code)}`, {
        method: "PUT",
        body: {
          desc: form.desc,
          division: form.division,
          manager: form.manager,
          client: form.client,
          value: form.value === "" ? null : Number(form.value),
          est_time: form.est_time === "" ? null : Number(form.est_time),
          isChargable: form.chargable,
          isResearchOrDevelopment: form.rd_flag,
          active: form.active ? 1 : 0,
        },
      });
      onClose();
    } catch (e) {
      setStatus(e?.message || "Save failed");
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Edit Project</h3>
        <div style={{ display: "grid", gap: 8 }}>
          <label>
            Code
            <input style={{ marginLeft: 16 }} value={project.code} disabled />
          </label>
          <label>
            Desc
            <input
              style={{ marginLeft: 16 }}
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
            />
          </label>
          <label>
            Manager
            <input
              style={{ marginLeft: 16 }}
              value={form.manager || ""}
              onChange={(e) => setForm({ ...form, manager: e.target.value })}
            />
          </label>
          <label>
            Client
            <input
              style={{ marginLeft: 16 }}
              value={form.client || ""}
              onChange={(e) => setForm({ ...form, client: e.target.value })}
            />
          </label>
          <label>
            Division
            <input
              style={{ marginLeft: 16 }}
              value={form.division || ""}
              onChange={(e) => setForm({ ...form, division: e.target.value })}
            />
          </label>
          <label>
            Est. Days
            <input
              style={{ marginLeft: 16 }}
              type="number"
              min="0"
              value={form.est_time ?? ""}
              onChange={(e) => setForm({ ...form, est_time: e.target.value })}
            />
          </label>
          <label>
            Project Value
            <input
              style={{ marginLeft: 16 }}
              type="number"
              min="0"
              value={form.value ?? ""}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            R&amp;D
            <input
              style={{ marginLeft: 16 }}
              type="checkbox"
              checked={!!form.rd_flag}
              onChange={(e) => setForm({ ...form, rd_flag: e.target.checked })}
            />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            Chargeable
            <input
              style={{ marginLeft: 16 }}
              type="checkbox"
              checked={!!form.chargable}
              onChange={(e) =>
                setForm({ ...form, chargable: e.target.checked })
              }
            />
          </label>
          <label>
            Active
            <input
              style={{ marginLeft: 16 }}
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onClose}>Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              style={{ background: "#2ecc71", color: "white" }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
          {status && <div style={{ color: "darkred" }}>{status}</div>}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedSection, setExpandedSection] = useState("create");

  function toggleSection(id) {
    setExpandedSection((cur) => (cur === id ? null : id));
  }

  // openOnly: open the named section (used when clicking the text label)
  function openOnly(id) {
    setExpandedSection(id);
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Settings</h2>

      <div style={{ marginTop: 12 }}>
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => openOnly("create")}
            title={expandedSection !== "create" ? "Expand" : "Collapse"}
          >
            Create User
          </span>
          <button
            type="button"
            onClick={() => toggleSection("create")}
            aria-expanded={expandedSection === "create"}
            aria-label={expandedSection !== "create" ? "Expand" : "Collapse"}
            style={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              border: "1px solid rgba(0,0,0,0.06)",
              background: "white",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              style={{
                transform:
                  expandedSection === "create"
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                transition: "transform 160ms ease",
                display: "block",
              }}
              aria-hidden
            >
              <path
                d="M6.23 8.21a1 1 0 011.41 0L12 12.6l4.36-4.39a1 1 0 111.41 1.42l-5.07 5.11a1 1 0 01-1.41 0L6.23 9.63a1 1 0 010-1.42z"
                fill="currentColor"
              />
            </svg>
          </button>
        </h3>
        {expandedSection === "create" && (
          <div style={{ marginTop: 8 }}>
            <CreateUserPanel onCreated={() => setRefreshKey((k) => k + 1)} />
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => openOnly("manage")}
            title={expandedSection !== "manage" ? "Expand" : "Collapse"}
          >
            Manage Users
          </span>
          <button
            type="button"
            onClick={() => toggleSection("manage")}
            aria-expanded={expandedSection === "manage"}
            aria-label={expandedSection !== "manage" ? "Expand" : "Collapse"}
            style={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              border: "1px solid rgba(0,0,0,0.06)",
              background: "white",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              style={{
                transform:
                  expandedSection === "manage"
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                transition: "transform 160ms ease",
                display: "block",
              }}
              aria-hidden
            >
              <path
                d="M6.23 8.21a1 1 0 011.41 0L12 12.6l4.36-4.39a1 1 0 111.41 1.42l-5.07 5.11a1 1 0 01-1.41 0L6.23 9.63a1 1 0 010-1.42z"
                fill="currentColor"
              />
            </svg>
          </button>
        </h3>
        {expandedSection === "manage" && (
          <div style={{ marginTop: 8 }}>
            <ManageUsers key={refreshKey} />
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => openOnly("projects")}
            title={expandedSection !== "projects" ? "Expand" : "Collapse"}
          >
            Projects
          </span>
          <button
            type="button"
            onClick={() => toggleSection("projects")}
            aria-expanded={expandedSection === "projects"}
            aria-label={expandedSection !== "projects" ? "Expand" : "Collapse"}
            style={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              border: "1px solid rgba(0,0,0,0.06)",
              background: "white",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              style={{
                transform:
                  expandedSection === "projects"
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                transition: "transform 160ms ease",
                display: "block",
              }}
              aria-hidden
            >
              <path
                d="M6.23 8.21a1 1 0 011.41 0L12 12.6l4.36-4.39a1 1 0 111.41 1.42l-5.07 5.11a1 1 0 01-1.41 0L6.23 9.63a1 1 0 010-1.42z"
                fill="currentColor"
              />
            </svg>
          </button>
        </h3>
        {expandedSection === "projects" && (
          <div style={{ marginTop: 8 }}>
            <CreateProjectPanel onCreated={() => setRefreshKey((k) => k + 1)} />
            <ManageProjects key={refreshKey} />
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <h3
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{ cursor: "pointer" }}
            onClick={() => openOnly("activities")}
            title={expandedSection !== "activities" ? "Expand" : "Collapse"}
          >
            Activities
          </span>
          <button
            type="button"
            onClick={() => toggleSection("activities")}
            aria-expanded={expandedSection === "activities"}
            aria-label={
              expandedSection !== "activities" ? "Expand" : "Collapse"
            }
            style={{
              width: 28,
              height: 28,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 6,
              border: "1px solid rgba(0,0,0,0.06)",
              background: "white",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              style={{
                transform:
                  expandedSection === "activities"
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                transition: "transform 160ms ease",
                display: "block",
              }}
              aria-hidden
            >
              <path
                d="M6.23 8.21a1 1 0 011.41 0L12 12.6l4.36-4.39a1 1 0 111.41 1.42l-5.07 5.11a1 1 0 01-1.41 0L6.23 9.63a1 1 0 010-1.42z"
                fill="currentColor"
              />
            </svg>
          </button>
        </h3>
        {expandedSection === "activities" && (
          <div style={{ marginTop: 8 }}>
            <CreateActivityPanel
              onCreated={() => setRefreshKey((k) => k + 1)}
            />
            <ManageActivities key={refreshKey} />
          </div>
        )}
      </div>
    </div>
  );
}

// Activities UI: Create, Manage, Edit overlay
function CreateActivityPanel({ onCreated }) {
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [active, setActive] = useState(true);
  const [status, setStatus] = useState("");

  async function submit(e) {
    e && e.preventDefault();
    setStatus("creating");
    if (!code) return setStatus("Activity code required");
    try {
      await apiFetch("/api/activities", {
        method: "POST",
        body: { activity: String(code).trim(), desc, active: active ? 1 : 0 },
      });
      setCode("");
      setDesc("");
      setActive(true);
      setStatus("created");
      onCreated && onCreated();
    } catch (e) {
      setStatus(e && e.message ? e.message : String(e));
    }
  }

  return (
    <form onSubmit={submit} style={styles.panel}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          placeholder="Activity code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          style={{ minWidth: 120 }}
        />
        <input
          placeholder="Description"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          Active
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(!!e.target.checked)}
          />
        </label>
        <button type="submit">Create</button>
      </div>
      {status && status !== "created" && (
        <div style={{ color: "darkred", marginTop: 8 }}>{String(status)}</div>
      )}
    </form>
  );
}

function ManageActivities() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState({ open: false });
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 10;

  async function load() {
    setLoading(true);
    try {
      const d = await apiFetch("/api/activities?inactive=true");
      const src = d && Array.isArray(d.activities) ? d.activities : [];
      setActivities(
        src.map((a) => ({
          code: a.code,
          desc: a.desc,
          active: !!a.active,
          __raw: a,
        })),
      );
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    setPage(0);
  }, [search, activeFilter, activities]);

  const filtered = activities.filter((a) => {
    if (activeFilter === "yes" && !a.active) return false;
    if (activeFilter === "no" && a.active) return false;
    if (!search || String(search).trim() === "") return true;
    const s = String(search).toLowerCase();
    return (
      String(a.desc || "")
        .toLowerCase()
        .includes(s) ||
      String(a.code || "")
        .toLowerCase()
        .includes(s)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(Math.max(0, page), Math.max(0, totalPages - 1));
  const paged = filtered.slice(
    safePage * pageSize,
    safePage * pageSize + pageSize,
  );

  function ask(action, act) {
    setConfirm({ open: true, action, act });
  }

  async function doConfirm() {
    const { action, act } = confirm;
    setConfirm({ open: false });
    if (!act) return;
    try {
      if (action === "delete" || action === "disable") {
        await apiFetch(`/api/activities/${encodeURIComponent(act.code)}`, {
          method: "DELETE",
        });
        load();
      }
      if (action === "enable") {
        await apiFetch(`/api/activities/${encodeURIComponent(act.code)}`, {
          method: "PATCH",
        });
        load();
      }
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <section style={{ marginTop: 12 }}>
      <div style={{ background: "#fbfbfe", borderRadius: 8, padding: 12 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              placeholder="Search activity or code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ddd",
                minWidth: 240,
              }}
            />
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              style={{
                padding: 6,
                borderRadius: 4,
                border: "1px solid #ddd",
              }}
            >
              <option value="all">All</option>
              <option value="yes">Active: Yes</option>
              <option value="no">Active: No</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 12, color: "#666" }}>
              {filtered.length} result{filtered.length === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage <= 0}
                style={styles.menuItem}
              >
                Prev
              </button>
              <div style={{ fontSize: 13 }}>
                {safePage + 1} / {totalPages}
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                style={styles.menuItem}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left" }}>
                <th>Code</th>
                <th>Description</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.map((a) => (
                <tr key={a.id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={{ padding: 8 }}>{a.code}</td>
                  <td style={{ padding: 8 }}>{a.desc}</td>
                  <td style={{ padding: 8 }}>{a.active ? "Yes" : "No"}</td>
                  <td style={{ padding: 8 }}>
                    <Menu>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 160,
                        }}
                      >
                        <button
                          onClick={() => setEditing(a)}
                          style={styles.menuItem}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() =>
                            ask(a.active ? "disable" : "enable", a)
                          }
                          style={{ ...styles.menuItem, color: "#c0392b" }}
                        >
                          {a.active ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ModalConfirm
        open={confirm.open}
        title={
          confirm.action === "delete" ? "Delete activity" : "Disable activity"
        }
        message={
          confirm.act
            ? `Are you sure you want to ${confirm.action} ${
                confirm.act.desc || confirm.act.code
              }?`
            : ""
        }
        onConfirm={doConfirm}
        onCancel={() => setConfirm({ open: false })}
      />

      {editing && (
        <EditActivityOverlay
          activity={editing}
          onClose={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function EditActivityOverlay({ activity, onClose }) {
  const [form, setForm] = useState({
    desc: activity.desc,
    active: !!activity.active,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await apiFetch(`/api/activities/${encodeURIComponent(activity.code)}`, {
        method: "PUT",
        body: { desc: form.desc, active: form.active ? 1 : 0 },
      });
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalBackdrop} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3>Edit Activity</h3>
        <div style={{ display: "grid", gap: 8 }}>
          <label>
            Code
            <input value={activity.code} disabled />
          </label>
          <label>
            Desc
            <input
              value={form.desc}
              onChange={(e) => setForm({ ...form, desc: e.target.value })}
            />
          </label>
          <label>
            Active
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
          </label>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onClose}>Cancel</button>
            <button
              onClick={save}
              disabled={saving}
              style={{ background: "#2ecc71", color: "white" }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  burgerButton: {
    padding: 6,
    borderRadius: 4,
    border: "1px solid #ddd",
    background: "white",
    cursor: "pointer",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "120%",
    background: "white",
    border: "1px solid #eee",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    borderRadius: 6,
    zIndex: 40,
    padding: 8,
  },
  menuItem: {
    background: "none",
    border: "none",
    padding: "8px 12px",
    textAlign: "left",
    cursor: "pointer",
  },
  modalBackdrop: {
    position: "fixed",
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    background: "white",
    padding: 16,
    borderRadius: 8,
    minWidth: 320,
    maxWidth: "90%",
    alignItems: "center",
    display: "grid",
    gridAutoColumns: "auto",
    justifyItems: "center",
  },
  panel: {
    background: "white",
    padding: 12,
    borderRadius: 8,
    border: "1px solid #eee",
    marginTop: 12,
  },
};
